import dayjs from 'dayjs'
import duration, { type Duration } from 'dayjs/plugin/duration'
import i18n from '@/scripts/app-configs/i18n-config'
import type { FormatDurationOptions } from '@/types/dayjs'

dayjs.extend(duration)

/**
 * Formats duration based on options
 * @param duration - dayjs Duration object to format
 * @param options - Formatting options
 * @returns Formatted string like "2h 30min 15sec" or "45min 30sec"
 */
function formatDurationVerbose(duration: Duration, options: Partial<FormatDurationOptions> = {}): string {
  const {
    hideZeroSeconds = true, // Hide seconds if they are zero
    showDays = true,
    showHours = true,
    showMinutes = true,
    showSeconds = true,
    alwaysShowMinutes = false,
  } = options

  const totalSeconds = duration.asSeconds()
  let days = duration.days()
  let hours = duration.hours()
  let minutes = duration.minutes()
  let seconds = duration.seconds()

  if (!showSeconds && seconds > 0) {
    minutes += Math.ceil(seconds / 60) // Round up seconds to minutes
    seconds = 0

    // Handle minute overflow
    if (minutes >= 60) {
      hours += Math.floor(minutes / 60)
      minutes = minutes % 60
    }

    // Handle hour overflow
    if (hours >= 24) {
      days += Math.floor(hours / 24)
      hours = hours % 24
    }
  }

  let result = ''

  if (days > 0 && showDays) {
    const dayLabel = days > 1 ? i18n.t('COMMON.DAYS') : i18n.t('COMMON.DAY')

    result += `${days} ${dayLabel.toString().toLowerCase()} `
  }

  if (hours > 0 && showHours) {
    result += `${hours}h `
  }

  if ((minutes > 0 || alwaysShowMinutes || hours > 0 || days > 0) && showMinutes) {
    result += `${minutes}min `
  }

  if (showSeconds && (!hideZeroSeconds || seconds > 0)) {
    result += `${seconds}sec`
  }

  if (!result.trim()) return hideZeroSeconds ? '0min' : totalSeconds < 60 ? `${Math.floor(totalSeconds)}sec` : '0sec'

  return result.trim()
}

/**
 * Formats duration as time format (HH:MM:SS or MM:SS) with rounding logic matching original formatDurationFromSeconds
 * @param duration - dayjs Duration object to format
 * @param options - Formatting options (showDays is ignored for time format)
 * @returns Formatted time string like "1:30:45" or "30:45" or "30" (when hideZeroSeconds is true)
 */
function formatDurationAsTimeFormat(duration: Duration, options: Partial<FormatDurationOptions> = {}): string {
  const {
    hideZeroSeconds = true,
    showHours = true,
    showMinutes = true,
    showSeconds = true,
    alwaysShowMinutes = false,
  } = options

  let hours = duration.hours()
  let minutes = duration.minutes()
  let seconds = duration.seconds()

  if (!showSeconds && seconds > 0) {
    minutes += Math.ceil(seconds / 60)
    seconds = 0

    if (minutes >= 60) {
      hours += Math.floor(minutes / 60)
      minutes = minutes % 60
    }
  }

  const parts = []

  if (hours > 0 && showHours) {
    parts.push(hours.toString())
  }

  if ((minutes > 0 || alwaysShowMinutes || (hours > 0 && showHours)) && showMinutes) {
    const minuteStr = (hours > 0 && showHours) ? minutes.toString().padStart(2, '0') : minutes.toString()

    parts.push(minuteStr)
  }

  if (showSeconds && (!hideZeroSeconds || seconds > 0)) {
    const secondStr = parts.length > 0 ? seconds.toString().padStart(2, '0') : seconds.toString()

    parts.push(secondStr)
  }

  return parts.join(':') || '0'
}

const durationPlugin = () => {
  dayjs.prototype.formatDuration = function formatDuration(
    seconds: number,
    options: FormatDurationOptions = {},
  ): string | undefined {
    if (seconds === null || isNaN(seconds)) return undefined

    const duration = dayjs.duration(seconds, 'seconds')

    return formatDurationVerbose(duration, options)
  }

  dayjs.prototype.formatDurationAsTime = function (
    seconds: number,
    options: FormatDurationOptions = {},
  ): string | undefined {
    if (seconds === null || isNaN(seconds)) return undefined
    const duration = dayjs.duration(seconds, 'seconds')

    return formatDurationAsTimeFormat(duration, options)
  }
}

export default durationPlugin
